import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {VisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Page, PageName} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {
    SurfaceBounds,
    SurfacePoint,
    SurfaceSize
} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {AnimationType, ISheetLocation, ISurfaceContext} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';

export class SurfaceContextImpl implements ISurfaceContext
{
    private static _SINGLETON:ISurfaceContext = new SurfaceContextImpl();

    public static getInstance():ISurfaceContext
    {
        return SurfaceContextImpl._SINGLETON;
    }

    public getBoxIdByName(boxName:string):BoxId
    {
        const designState = commonControllers.designState.getDesignState();

        if( !document || !designState || !designState.surface )
            return null;

        const box:Box = Array.from(designState.surface.activePage.artboard.allBoxes.values()).find(
            (box:Box) => box.componentDesc.name && box.componentDesc.name.value === boxName
        );

        if( box )
            return box.id;
        else
            return null;
    }

    public setActivePage(pageName:string, isAnimated:boolean):boolean
    {
        return commonControllers.designState.setActivePageByName(pageName, isAnimated);
    }

    public getActivePageName():string
    {
        return commonControllers.designState.getActivePageName();
    }

    public showOverlay(pageName:string, isAnimated:boolean):boolean
    {
        return commonControllers.designState.showOverlayByName(pageName, isAnimated);
    }

    public hideOverlay(pageName:PageName, hideAllAbove:boolean, isAnimated:boolean):boolean
    {
        return commonControllers.designState.hideOverlayByName(pageName, hideAllAbove, isAnimated);
    }

    public toggleOverlay(pageName:PageName, hideAllAbove:boolean, isAnimated:boolean):boolean
    {
        return commonControllers.designState.toggleOverlay(pageName, hideAllAbove, isAnimated);
    }

    public hideTopmostOverlay(isAnimated:boolean):boolean
    {
        return commonControllers.designState.hideTopmostOverlay(isAnimated);
    }

    public hideAllOverlays(isAnimated:boolean):boolean
    {
        return commonControllers.designState.hideAllOverlays(true, isAnimated);
    }

    public async addComponent(pageName:string,
                              parentComponentName:string = null,
                              componentType:string,
                              componentName?:string,
                              xOffsetInParent?:number,
                              yOffsetInParent?:number,
                              width?:number,
                              height?:number):Promise<boolean | Error>
    {
        const page:Page = commonControllers.surfaceArea.findPageByName(pageName);

        if( !page )
            return new Error(`Page with the name ${pageName} doesn't exist`);

        let parentBox:Box = page.artboard.viewport;

        if( parentComponentName )
        {
            parentBox = commonControllers.surfaceArea.findBoxByName(page.id, parentComponentName);

            if( !parentBox )
                return new Error(`Parent component with the name ${parentComponentName} doesn't exist`);
        }

        const component:VisualComponent = Array.from(commonControllers.artifacts.getVisualComponents().values())
            .find((component:VisualComponent) => componentType === component.name);

        if( !component )
            return new Error(`Component type ${componentType} is not valid`);

        if( !xOffsetInParent )
            xOffsetInParent = 0;

        if( !yOffsetInParent )
            yOffsetInParent = 0;

        if( !width || !height )
        {
            const initialSize:SurfaceSize = commonControllers.surfaceArea.componentInitialSize(component);

            width = width || initialSize.width;
            height = height || initialSize.height;
        }

        const box:Box = await commonControllers.surfaceArea.addNewBox(
            page,
            parentBox,
            null,
            component,
            componentName,
            SurfacePoint.from(xOffsetInParent, yOffsetInParent),
            new SurfaceSize(width, height)
        );

        box.componentDesc.removeLinkNameToProp();

        return !!box;
    }

    public async copyComponent(srcPageName:string,
                               srcComponentName:string,
                               destPageName:string,
                               destParentComponentName:string = null,
                               newComponentName:string,
                               keepBindings:boolean = true,
                               xOffsetInParent?:number,
                               yOffsetInParent?:number,
                               width?:number,
                               height?:number):Promise<boolean | Error>
    {
        const srcPage:Page = commonControllers.surfaceArea.findPageByName(srcPageName);

        if( !srcPage )
            return new Error(`Page with the name ${srcPageName} doesn't exist`);

        const srcBox:Box = commonControllers.surfaceArea.findBoxByName(srcPage.id, srcComponentName);

        if( !srcBox )
            return new Error(`Component with the name ${srcComponentName} doesn't exist`);

        const destPage:Page = commonControllers.surfaceArea.findPageByName(destPageName);

        if( !destPage )
            return new Error(`Page with the name ${destPageName} doesn't exist`);

        let destParentBox:Box = destPage.artboard.viewport;

        if( destParentComponentName )
        {
            destParentBox = commonControllers.surfaceArea.findBoxByName(destPage.id, destParentComponentName);

            if( !destParentBox )
                return new Error(`Parent component with the name ${destParentComponentName} doesn't exist`);
        }

        if( commonControllers.surfaceArea.findBoxByName(destPage.id, newComponentName) )
            return new Error(`Component with the name ${newComponentName} already exists`);

        if( !xOffsetInParent )
            xOffsetInParent = 0;

        if( !yOffsetInParent )
            yOffsetInParent = 0;

        if( !width || !height )
        {
            const initialSize:SurfaceSize = commonControllers.surfaceArea.componentInitialSize(srcBox.component);

            width = width || initialSize.width;
            height = height || initialSize.height;
        }

        const newBox:Box = await commonControllers.surfaceArea.cloneBox(
            srcPage,
            destPage,
            destParentBox,
            srcBox,
            newComponentName,
            keepBindings,
            SurfaceBounds.from(xOffsetInParent, yOffsetInParent, width, height)
        );

        newBox.componentDesc.removeLinkNameToProp();

        return !!newBox;
    }

    public removeAllComponents(pageName:string):boolean | Error
    {
        const page:Page = commonControllers.surfaceArea.findPageByName(pageName);

        if( !page )
            return new Error(`Page with the name ${pageName} doesn't exist`);

        page.artboard.viewport.children.slice().forEach(commonControllers.surfaceArea.removeVisualComponent);

        return true;
    }

    public removeComponents(pageName:string, parentComponentName:string):boolean | Error
    {
        const page:Page = commonControllers.surfaceArea.findPageByName(pageName);

        if( !page )
            return new Error(`Page with the name ${pageName} doesn't exist`);

        const parentBox:Box = commonControllers.surfaceArea.findBoxByName(page.id, parentComponentName);

        if( !parentBox )
            return new Error(`Parent component with the name ${parentComponentName} doesn't exist`);

        parentBox.children.slice().forEach(commonControllers.surfaceArea.removeVisualComponent);

        return true;
    }

    public removeComponent(pageName:string, componentName:string):boolean | Error
    {
        const page:Page = commonControllers.surfaceArea.findPageByName(pageName);

        if( !page )
            return new Error(`Page with the name ${pageName} doesn't exist`);

        const box:Box = commonControllers.surfaceArea.findBoxByName(page.id, componentName);

        if( !box )
            return new Error(`Component with the name ${componentName} doesn't exist`);

        commonControllers.surfaceArea.removeVisualComponent(box);

        return true;
    }

    public setBinding(pageName:string,
                      componentName:string,
                      propOrEventFullPath:string,
                      sheetName:string,
                      cellLocation:ISheetLocation,
                      pathInFrame:string,
                      fixedDirection:string,
                      useFormat:boolean = false,
                      direction:string,
                      itemsCount:number):boolean | Error
    {
        return commonControllers.bindings.setBinding(
            pageName,
            componentName,
            propOrEventFullPath,
            sheetName,
            cellLocation,
            pathInFrame,
            fixedDirection,
            useFormat,
            direction,
            itemsCount
        );
    }

    public async animate(pageName:string,
                         componentName:string,
                         repeaterLevels:Array<number>,
                         animationType:AnimationType,
                         options:IFrame):Promise<boolean | Error>
    {
        const page:Page = commonControllers.surfaceArea.findPageByName(pageName);

        if( !page )
            return new Error('#PAGE_NOT_FOUND');

        const box:Box = commonControllers.surfaceArea.findBoxByName(page.id, componentName);

        if( !box )
            return new Error('#COMPONENT_NOT_FOUND');

        return commonControllers.animation.animate(box, repeaterLevels, animationType, options);
    }

    public stopAnimations(pageName:string, componentName:string, repeaterLevels:Array<number>):boolean | Error
    {
        const page:Page = commonControllers.surfaceArea.findPageByName(pageName);

        if( !page )
            return new Error(`Page with the name ${pageName} doesn't exist`);

        const box:Box = commonControllers.surfaceArea.findBoxByName(page.id, componentName);

        if( !box )
            return new Error(`Component with the name ${componentName} doesn't exist`);

        commonControllers.animation.stopAnimations(box, repeaterLevels);

        return true;
    }
}
